// File: frontend/src/components/ExploitationBadges.tsx
// Shared badges for real-world exploitation signals (CISA KEV + EPSS).
// Used by CVETable, CVESearchPage and the Patch Priority view.
import React from 'react';
import { Chip, Tooltip, alpha } from '@mui/material';
import GppBadIcon from '@mui/icons-material/GppBad';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export interface ExploitationSignals {
    epss_score?: number | null;
    epss_percentile?: number | null;
    in_cisa_kev?: boolean;
    kev_date_added?: string | null;
    kev_due_date?: string | null;
    kev_ransomware_use?: boolean;
    risk_score?: number | null;
}

const KEV_COLOR = '#d32f2f';

export const KevChip: React.FC<{
    dateAdded?: string | null;
    dueDate?: string | null;
    ransomwareUse?: boolean;
}> = ({ dateAdded, dueDate, ransomwareUse }) => {
    const tooltipLines = [
        'Listed in the CISA Known Exploited Vulnerabilities catalog',
        dateAdded ? `Added: ${dateAdded}` : null,
        dueDate ? `Remediation due: ${dueDate}` : null,
        ransomwareUse ? '⚠️ Known use in ransomware campaigns' : null,
    ].filter(Boolean);

    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipLines.join('\n')}</span>}>
            <Chip
                icon={<GppBadIcon sx={{ fontSize: 14 }} />}
                label={ransomwareUse ? 'KEV · RANSOMWARE' : 'KEV'}
                size="small"
                sx={{
                    bgcolor: alpha(KEV_COLOR, 0.18),
                    color: KEV_COLOR,
                    fontWeight: 'bold',
                    fontSize: '0.65rem',
                    height: 20,
                    border: `1px solid ${alpha(KEV_COLOR, 0.45)}`,
                    '& .MuiChip-icon': { color: KEV_COLOR },
                }}
            />
        </Tooltip>
    );
};

// EPSS color ramp: green < 10% < amber < 50% < red
const epssColor = (score: number): string => {
    if (score >= 0.5) return '#ff5c5c';
    if (score >= 0.1) return '#ffb300';
    return '#66bb6a';
};

export const EpssChip: React.FC<{
    score?: number | null;
    percentile?: number | null;
}> = ({ score, percentile }) => {
    if (score === null || score === undefined) return null;
    const color = epssColor(score);
    // Keep a decimal near the extremes: 0.996 must read "99.6%", not a
    // misleading "100%" (EPSS is a probability, never certainty).
    const pctNum = score * 100;
    const pct = (pctNum >= 99.5 && pctNum < 100) || pctNum < 10
        ? pctNum.toFixed(1)
        : pctNum.toFixed(0);
    const tooltip = `EPSS: ${(score * 100).toFixed(2)}% probability of exploitation in the next 30 days`
        + (percentile !== null && percentile !== undefined
            ? ` (higher than ${(percentile * 100).toFixed(0)}% of all CVEs)`
            : '');

    return (
        <Tooltip title={tooltip}>
            <Chip
                icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                label={`EPSS ${pct}%`}
                size="small"
                sx={{
                    bgcolor: alpha(color, 0.15),
                    color: color,
                    fontWeight: 'bold',
                    fontSize: '0.65rem',
                    height: 20,
                    border: `1px solid ${alpha(color, 0.35)}`,
                    '& .MuiChip-icon': { color: color },
                }}
            />
        </Tooltip>
    );
};

/** Compact horizontal stack of KEV + EPSS badges; renders nothing when there is no signal. */
export const ExploitationBadgeStack: React.FC<{ signals: ExploitationSignals }> = ({ signals }) => {
    const hasKev = !!signals.in_cisa_kev;
    const hasEpss = signals.epss_score !== null && signals.epss_score !== undefined;
    if (!hasKev && !hasEpss) return null;

    return (
        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasKev && (
                <KevChip
                    dateAdded={signals.kev_date_added}
                    dueDate={signals.kev_due_date}
                    ransomwareUse={signals.kev_ransomware_use}
                />
            )}
            {hasEpss && <EpssChip score={signals.epss_score} percentile={signals.epss_percentile} />}
        </span>
    );
};
